import { ANSWER_FACET, answerRefEligibility, answerRefReach, deriveGraph, familyNameOf, type AnswerRef, type AnswerRefAlias, type GraphTask } from '@agavi/blocks';
import { Link, Link2, Link2Off } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { answerRefDefaultName, useAnswerRefScope, useAtExit } from './answer-ref-context';
import { BLOCK_META } from './block-library';
import { useBlockIdentity } from './fired-states';

import type { Block, Task } from '@/types/experience';

import { FlowPickerMenu, type PickerOption, type PickerSection } from '@/components/task/flow-picker-menu';
import { useCapabilities } from '@/hooks/use-capabilities';
import { recallAllowedInBlock } from '@/lib/recall-rungs';

/**
 * THE ONE WAY TO PICK AN EARLIER ANSWER.
 *
 * Reuses Flow's picker for the same reason the variable picker does: a closed set of options,
 * grouped, too many to scan, and a second keyboard implementation is a second one to keep
 * honest. What it adds is that an option can be UNPICKABLE WITH A REASON, which `PickerOption`
 * already supports and which this menu depends on completely.
 *
 * ## Every INPUT block is listed, including what cannot be used
 *
 * Hiding an unusable input means a teacher cannot tell "that block does not exist" from "that
 * block is not allowed here", and they go hunting for the difference. Listing it greyed, with
 * the reason, teaches the rule instead.
 *
 * Display blocks are a different case and are left out entirely. Nobody looks for the answer to
 * a paragraph, so "no answer here" teaches nothing — it just pushes the blocks that DO have
 * answers below a wall of text, image and chart rows. `notAnInput` is the reason that means
 * "do not list this"; `noAnswer` (a fill-in-the-blank with no blanks) still appears, because
 * that one the teacher can fix.
 *
 * ## The whole, with its parts nested under it
 *
 * A GPS answer is a coordinate AND a latitude AND a longitude; a Numerical with units is a
 * reading AND a number AND a unit. Flat, those read as three unrelated rows that happen to
 * share a prefix. Nested under the combined facet they read as what they are — one answer, and
 * the pieces of it — and the row a teacher wants most often is the one at the top.
 *
 * ## Grouped by task, and the icon is what disambiguates
 *
 * Block names are the teacher's, so six of them can be called "Kayak". The type ICON is
 * invariant under renaming, so three shapes still read as three different blocks. The question
 * text goes in as searchable content because a teacher remembers "the one where they picked a
 * hypothesis", not "MCQ 2".
 */

/** HTML down to the words in it, which is all a search or a row label needs. */
const plainWords = (raw: string): string =>
  raw.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

/**
 * The strip of HTML a question is, reduced to something a search can match and a row can show.
 *
 * Exported because the LINK picker wants the identical thing for the identical reason — a
 * teacher remembers "the one where they picked a hypothesis", not "MCQ 2" — and two strippers
 * would disagree the first time one of them learned about a new inline element.
 */
export function questionSummary(block: Block): string {
  const data = (block.data ?? {}) as { question?: string; html?: string };
  return plainWords(data.question ?? data.html ?? '');
}

/**
 * EVERY ANSWER A BLOCK OFFERS, as searchable words.
 *
 * The other half of how a teacher remembers a block. Asked for on review: a question is often
 * the generic part — "Which of these?" — while the ANSWERS are the specific one, so "Titan" or
 * "oxygen" is what comes to mind and what gets typed.
 *
 * ## Named fields rather than a walk of the whole object
 *
 * A recursive sweep for anything called `text` would pull in feedback bodies, branch labels and
 * option FORMULAS, and a teacher typing "Mars" would match a block that merely says Mars in a
 * wrong-answer message. These five keys are where a block keeps the things a student can
 * choose: `options` (mcq, dropdown, keyword), `values` (scale), `items` (ordering, sort),
 * `bins` (sort's targets) and `blanks[].words` (FITB, which nests one list per blank).
 *
 * The cost of the named list is that a new block's answers are unsearchable until it is added
 * here. That is the right way round: silence is a missing feature, where a walk's false matches
 * are a search that cannot be trusted.
 */
export function answerOptionSummary(block: Block): string {
  const data = (block.data ?? {}) as Record<string, unknown>;
  const words: string[] = [];
  const take = (v: unknown) => { if (typeof v === 'string' && v) words.push(v); };
  const takeAll = (arr: unknown) => {
    if (!Array.isArray(arr)) return;
    for (const entry of arr) {
      const o = (entry ?? {}) as Record<string, unknown>;
      take(o.text);
      take(o.label);
      // FITB keeps a list of accepted words per blank, so its answers are one level down.
      takeAll(o.words);
    }
  };
  for (const key of ['options', 'values', 'items', 'bins', 'blanks']) takeAll(data[key]);
  return plainWords(words.join(' '));
}

/**
 * HOW OFTEN AN ANSWER IS THERE TO BE QUOTED, as a glyph — a whole chain, a broken one, none.
 *
 * Exported because the driven-by menu draws the same ladder about the same graph, and the two
 * were separate: that menu wrote a bare `?` and this one wrote a chain, for one fact. A teacher
 * met two vocabularies for "can a student here have answered it".
 */
export const REACH_ICON = { always: Link2, sometimes: Link, never: Link2Off } as const;

/**
 * And what it says out loud. A glyph alone reaches nobody using a screen reader, and `sometimes`
 * is the state with no other signal on the row — `never` at least greys it with a reason.
 */
export const REACH_LABEL = {
  always: 'blocks.answerRef.reach.always',
  sometimes: 'blocks.answerRef.reach.sometimes',
  never: 'blocks.answerRef.reach.never',
} as const;

/**
 * WHICH FACETS A SURFACE CAN TAKE.
 *
 * `'number'` is the formula surfaces: an equation does arithmetic, so a recall that resolves to
 * words, a range, a pair or a measure has nothing to contribute to one. The facets already
 * declare which they are — `AnswerFacet.kind`, per instance, so a dual slider offers two handles
 * and a single one offers its bare value — and this is the first thing to read it.
 *
 * `'any'` is prose, where every facet reads as something.
 */
export type FacetKindFilter = 'any' | 'number';

/**
 * A picker row that still knows WHICH ADDRESS it offers.
 *
 * The manager asks a second question of this list — "could anything here join the name?" — and
 * answering it needs the address, not the label. It used to be answerable from `disabled`
 * alone, until "already part of this name" had to stop counting as a reason equivalence is
 * impossible: an address the name already holds is not a candidate that failed, it is the
 * name's own member looking back.
 *
 * Carried here rather than on `PickerOption`, which is Flow's and knows nothing of addresses.
 * A row for a block that cannot be referenced at all has none, because it stands for the
 * block rather than for one of its answers.
 */
export interface AnswerRefOption extends PickerOption {
  ref?: AnswerRef;
}

export interface AnswerRefSection extends PickerSection {
  options: AnswerRefOption[];
}

export function answerRefSections(
  tasks: Task[],
  currentTaskId: string,
  t: (k: string, o?: Record<string, unknown>) => string,
  aliases: AnswerRefAlias[] = [],
  /**
   * Addresses another alias already owns, greyed with a reason.
   *
   * One address belongs to at most one alias, or "renaming is universal" is false. Offering an
   * address that is already spoken for and refusing it afterwards would teach the rule by
   * failure; greying it teaches the rule by showing it, which is the same bargain the blocked
   * reasons above make.
   */
  unavailable?: (ref: AnswerRef) => string | undefined,
  /** Which facets this surface can take — see `FacetKindFilter`. */
  kinds: FacetKindFilter = 'any',
  /**
   * WHERE THE PROSE BEING WRITTEN LIVES, which decides what the current task can offer.
   *
   * `atExit` is the whole of it: at the exit the task's own answers all exist, so they are
   * offered live; in a block they do not exist yet when the block renders, so they are listed
   * and refused. `answerRefReach` carries that rule and this only passes the fact along.
   *
   * `selfBlockId` is the block the picker is standing in, which gets a sharper refusal than
   * its siblings — "a question cannot recall its own answer" rather than "not answered before
   * here", because the two are different mistakes.
   */
  site?: { atExit?: boolean; selfBlockId?: string },
  /**
   * THE SOURCE HALF OF THE RECALL RUNG — which answers may be offered at all.
   *
   * Recall widens along two axes and the control answers only one of them: a button is offered
   * before a source is chosen, so the DESTINATION decides whether it appears and this decides
   * what is inside it. A teacher at rung 1 standing in a Numerical block therefore sees the
   * button and is offered numerical answers alone, rather than a button that refuses
   * everything it opens.
   *
   * Only the source needs checking here. The affordance has already established that the
   * destination is admitted at this rung, so `recallAllowedBetween` reduces to its source half.
   *
   * Undefined means no rung is known and nothing is filtered — a caller that has not been
   * given one must not have a gate invented for it.
   */
  sourceRung?: number,
): AnswerRefSection[] {
  const sections: AnswerRefSection[] = [];
  /* Both halves of a derived name, carried together — see `NameSource`. */
  const nameSrc = { tasks, aliases };

  /*
   * SPINE FIRST, THEN PATHWAYS, which is how Flow's destination picker already orders itself
   * and therefore how a teacher has learned to read a list of tasks in this product.
   *
   * It was raw array order, which interleaves the two silently. The grouping earns its keep
   * because a spine answer and a pathway answer are different KINDS of thing to quote: one is
   * on the route everybody takes, the other is on a route some of them took. Seeing them in
   * separate runs says that before any icon has to.
   *
   * It is a cue, not a proof. A goto can skip a spine task and a pathway can be the only way
   * through, so the per-row reach icon remains the thing that actually knows. A pathway's
   * header names the task it hangs off, so a teacher looking for it "near" its anchor still
   * finds it without the two kinds being mixed back together.
   */
  const graph = deriveGraph(tasks as GraphTask[]);
  const byId = new Map(tasks.map(tk => [tk.id, tk]));
  const spine = graph.spine.map(id => byId.get(id)).filter((tk): tk is Task => !!tk);
  const onSpine = new Set(spine.map(tk => tk.id));
  const ordered: { task: Task; pathway?: string }[] = [
    ...spine.map(task => ({ task })),
    // Pathways after the spine, ordered by where the task they hang off SITS on it. The
    // anchor is the thing a teacher looks for a detour near, and position says it without
    // spending a third of the header on repeating a task title they can already see above.
    ...tasks
      .filter(tk => !onSpine.has(tk.id))
      .map(task => ({ task, anchorAt: graph.spine.indexOf(task.pathwayOf ?? '') }))
      .sort((a, b) => a.anchorAt - b.anchorAt)
      .map(({ task }) => ({
        task,
        pathway: task.pathwayName || t('experience.builder.pathwayUnnamed'),
      })),
  ];

  for (const { task, pathway } of ordered) {
    /*
     * THE CURRENT TASK IS IN THE LIST. It used to be skipped outright — same-task references
     * were parked, and the comment here said the task was dropped rather than greyed because
     * it was "not a thing the teacher could fix".
     *
     * Un-parked by the owner: "the current question should always be in the recall menu
     * everywhere, otherwise the structure of the activity becomes confusing." A teacher writing
     * "you said {speed}" on the exit of the task that asked for the speed was looking at a menu
     * with no speed in it, and nothing on screen said why.
     */
    const options: AnswerRefOption[] = [];
    for (const block of task.blocks ?? []) {
      /*
       * ITSELF IS LISTED, AND REFUSED — see `site.selfBlockId`. It was omitted, and the owner's
       * correction is the rule this menu already follows everywhere else: "same block recalls
       * need to be listed in the menu for completeness, but should be disabled."
       *
       * A row that is simply absent teaches nothing — a teacher looking for this question's own
       * answer concludes the feature is broken or that they have mis-remembered the block. A
       * greyed row with a reason answers the question they were about to ask, which is the
       * bargain every other refusal on this menu makes.
       */
      const isSelf = block.id === site?.selfBlockId;
      const data = (block.data ?? {}) as Record<string, unknown>;
      const eligibility = answerRefEligibility(block.type, data);
      // The type's own icon, from the library's map — invariant under renaming, which is what
      // separates six blocks a teacher has called "Kayak".
      const Icon = BLOCK_META[block.type]?.Icon;
      const name = block.name ?? t('blocks.answerRef.unnamedSource');
      const summary = questionSummary(block);

      /*
       * Nothing to explain, so no row at all — see the note above.
       *
       * `ordering` joins `notAnInput` because it is the same KIND of fact: a permanent
       * property of the type, not a state of this block. The other reasons are things a
       * teacher can go and change — take the image off the options, make it graded, give it
       * an answer — and those earn a greyed row saying so. "Ordering is never referenceable"
       * earns nothing but a line of noise in every menu, forever.
       */
      if (!eligibility.referenceable
        && (eligibility.reason === 'notAnInput' || eligibility.reason === 'ordering')) continue;

      /* One row for the block, not one per facet: the refusal is about the BLOCK, and three
         greyed lines saying the same thing is the noise the eligibility rules above avoid. */
      if (isSelf) {
        options.push({
          key: `${block.id}:-`,
          text: `${name} ${summary} ${answerOptionSummary(block)}`,
          disabled: t('blocks.answerRef.blocked.itself'),
          node: <span className='flex items-center gap-1.5'>{Icon && <Icon className='h-3.5 w-3.5' />}{name}</span>,
        });
        continue;
      }

      /*
       * ABOVE THE RUNG: not listed at all, rather than listed and refused.
       *
       * The blocked branch below exists for answers a teacher CAN unblock — make the block
       * non-optional, take the image off the option — so it names a repair. A rung is not a
       * property of the block and has no repair on this screen, so listing it would be a row
       * whose only remedy is somewhere else entirely.
       */
      if (sourceRung !== undefined && !recallAllowedInBlock(sourceRung, block.type)) continue;

      if (!eligibility.referenceable) {
        options.push({
          key: `${block.id}:-`,
          // The summary joins the text so search still finds a blocked block — a teacher looking
          // for it should find it and read why, not conclude it is gone.
          text: `${name} ${summary} ${answerOptionSummary(block)}`,
          disabled: t(`blocks.answerRef.blocked.${eligibility.reason}`),
          node: <span className='flex items-center gap-1.5'>{Icon && <Icon className='h-3.5 w-3.5' />}{name}</span>,
        });
        continue;
      }

      // Reachability is a property of the TASK pair, so it is the same for every facet of a
      // block — computed once per block rather than once per row.
      // The SITE, not just the task pair: a same-task answer reads `never` in a block and
      // `always` at the exit, and the disabled branch below follows from it with no second rule.
      const reach = answerRefReach(task.id, currentTaskId, tasks, { atExit: site?.atExit });
      const ReachIcon = REACH_ICON[reach];

      // The combined facet leads and the rest indent under it, where there is one. A block
      // without one (a fill-in-the-blank's blanks, a single-facet block) has no whole to nest
      // its parts beneath, so its rows stay flat rather than growing an unselectable header.
      /*
       * FILTER FIRST, THEN DECIDE WHAT NESTS UNDER WHAT.
       *
       * `nested` used to be read off the unfiltered list, which was right while every facet
       * was offered and wrong the moment one surface stopped offering them all. A `combined`
       * facet is always TEXT, so a formula filters it out — and every surviving facet still
       * counted itself a child of it, drawing the indent corner that REPLACES the block's
       * icon. The menu lost its block symbols and showed children with no parent above them.
       */
      const offered = eligibility.facets.filter(f => kinds === 'any' || f.kind === kinds);
      const combined = offered.find(f => f.id === ANSWER_FACET.combined);
      const ordered = combined
        ? [combined, ...offered.filter(f => f !== combined)]
        : offered;

      for (const facet of ordered) {
        const ref: AnswerRef = { taskId: task.id, blockId: block.id, facet: facet.id };
        /*
         * Short form: the section heading above these rows is already the task's name.
         *
         * THE BLOCK'S OWN SPELLING, because this is the line that says what the row points
         * AT — see the stacking note on `node` below. A family name belongs on the line
         * above it, where `named` is.
         */
        const label = answerRefDefaultName(nameSrc, ref, t, { omitTask: true, ignoreFamily: true });
        const nested = !!combined && facet !== combined;
        const taken = unavailable?.(ref);
        /*
         * A NAMED reference shows its name HERE, under the question it came from, with the
         * question's own name greyed beside it.
         *
         * It had its own section at the top for a while. That made the named ones impossible
         * to place: the same answer appeared twice in the menu under two different words, and
         * neither row said it was the other. Keeping one row, sorted where the answer actually
         * lives, means the alias is something you recognise rather than something you have to
         * match up.
         *
         * ## By FAMILY, not by this exact address
         *
         * Naming the number `kayak-vel` makes the unit `kayak-vel (unit)` everywhere else, so
         * a menu that went on calling that row "Numerical 1 (unit)" would be the one surface
         * where the two halves of one question do not look related — and it is the surface a
         * teacher is on when they go looking for the other half. A facet with no alias of its
         * own still wears the name, because the name belongs to the block.
         *
         * With the suffix, because a family is several rows here and a column of identical
         * `kayak-vel` names none of them.
         */
        const named = familyNameOf(ref, aliases)
          ? answerRefDefaultName(nameSrc, ref, t, { omitTask: true })
          : null;
        options.push({
          key: `${block.id}:${facet.id}`,
          ref,
          // Both names are searchable: a teacher may remember either.
          text: `${named ?? ''} ${label} ${summary} ${answerOptionSummary(block)}`,
          /*
           * `never` is the only one that cannot be inserted.
           *
           * `sometimes` became insertable with aliases: the empty-value story it was waiting
           * for is here, because an alias can carry a second member covering the other path
           * and a fallback for when neither is reached. It still shows its reach icon, which
           * says "this depends on the route" without calling it a mistake — it is a
           * legitimate design as often as not, and only the teacher knows which.
           *
           * `taken` outranks both: an address another alias holds cannot be used here at all,
           * whatever its reach.
           */
          disabled: taken ?? (reach === 'never' ? t('blocks.answerRef.reach.never') : undefined),
          node: (
            /*
             * A RENAMED ANSWER STACKS: its own name on top, what it points at beneath.
             *
             * Side by side the two competed for one row, and whichever gave way was the one
             * someone needed — a block named like a whole question squeezed the alias to
             * nothing, and protecting the alias clipped the block name instead. Stacked,
             * each gets the full width and the question stops being which to sacrifice.
             *
             * It is also the shape the panel already uses — name, then the address under it
             * in smaller muted type — so the two surfaces teach the same thing once.
             *
             * Unnamed rows stay a single line. There is nothing to stack, and giving every
             * row two lines would halve what a scrolling menu can show for no gain.
             */
            <span className={`flex min-w-0 items-center gap-1.5 whitespace-nowrap${nested ? ' pl-4' : ''}`}>
              {/* The type icon belongs to the BLOCK, so a nested part does not repeat it — the
                  indent already says which block it belongs to, and four copies of the same
                  glyph in a column is noise. The corner rule takes its place. */}
              {nested
                ? <span aria-hidden='true' className='w-3.5 border-l border-b border-ws-border h-2 -mt-2 ml-1 rounded-bl-[2px]' />
                : Icon && <Icon className='h-3.5 w-3.5' />}
              <ReachIcon aria-hidden='true' className='h-3 w-3 opacity-70' />
              <span className='sr-only'>{t(REACH_LABEL[reach])}</span>
              {/* No brackets: the parentheses belong to the FACET, and `their gas (Keyword 1
                  (unit))` nests them two deep. Greyed and italic instead, which separates the
                  answer's own name from the alias without spending punctuation that already
                  means something else here. */}
              <span className='flex min-w-0 flex-col'>
                <span className='truncate'>{named ?? label}</span>
                {named && <span className='truncate text-[0.6875rem] leading-tight italic text-ws-muted'>{label}</span>}
              </span>
            </span>
          ),
        });
      }
    }

    if (options.length > 0) {
      /*
       * The pathway rides on the task's own header, so a row stays short enough to read, and
       * the CURRENT task says so in the same place for the same reason.
       *
       * It needs saying: every other section is somewhere the student has already been, and
       * this one is where they are standing. A teacher scanning for "the question above this
       * one" should not have to recognise the title to know which section is here and now —
       * and in a copied task the titles repeat, so recognising it is not always possible.
       */
      const here = task.id === currentTaskId ? t('blocks.answerRef.thisTask') : null;
      const suffix = pathway ?? here;
      sections.push({
        header: suffix ? `${task.title} \u00b7 ${suffix}` : task.title,
        headerIsName: true,
        options,
      });
    }
  }

  return sections;
}

export function AnswerRefPickerMenu({ tasks, currentTaskId, onChoose, ariaLabel, renderTrigger, handleRef, onDismiss, className, unavailable, kinds = 'any' }: {
  tasks: Task[];
  currentTaskId: string;
  /** The chosen address, already split back out of the picker's composite key. */
  onChoose: (ref: AnswerRef) => void;
  /** See `answerRefSections` — addresses another alias already holds. */
  /**
   * WHY THIS CANDIDATE CANNOT BE PICKED, greyed with the reason on the row.
   *
   * Was `ownedElsewhere`, when the only reason was that another alias already held the
   * address. Adding a member now also has to satisfy the rules a MULTI-MEMBER alias lives by
   * — one route may not answer two members, and they must be the same block type read on the
   * same facet — so the prop is named for what it does rather than for the first reason it
   * had. One hook, several reasons, rather than a second mechanism that could disagree.
   */
  unavailable?: (ref: AnswerRef) => string | undefined;

  ariaLabel: string;
  renderTrigger: Parameters<typeof FlowPickerMenu>[0]['renderTrigger'];
  handleRef?: Parameters<typeof FlowPickerMenu>[0]['handleRef'];
  onDismiss?: () => void;
  className?: string;
  /** Which facets this surface can take. A formula asks for `'number'`; prose takes any. */
  kinds?: FacetKindFilter;
}) {
  const { t } = useTranslation();
  const scope = useAnswerRefScope();
  /*
   * WHICH BLOCK THIS MENU IS STANDING IN, so it can leave itself out.
   *
   * From context rather than a prop: the picker is opened from a dozen places and every one of
   * them is already inside the `BlockIdProvider` that the canvas wraps each block in. A prop
   * would be a dozen call sites to remember, and the one that forgot would quietly offer a
   * question its own answer.
   *
   * `null` where there is no such block — the exit editor — and there nothing is excluded,
   * which is right: an exit is read after every question in the task has been answered.
   */
  const identity = useBlockIdentity();
  /* The exit offers the current task's answers; a block lists and refuses them. */
  const atExit = useAtExit();
  /* The SOURCE half of the rung, read here so every mount of this menu is filtered the same
     way — the picker is opened from a dozen places and a prop would be a dozen to remember. */
  const { level } = useCapabilities();
  const sourceRung = level('answerRecall');
  const sections = useMemo(
    // Keyed on `scope`, not on `aliases`: the array is rebuilt on every render when the scope
    // object is, so depending on it directly would defeat the memo entirely.
    () => answerRefSections(tasks, currentTaskId, t, scope?.aliases ?? [], unavailable, kinds,
      { atExit, selfBlockId: identity?.blockId }, sourceRung),
    [tasks, currentTaskId, t, scope, unavailable, kinds, identity?.blockId, atExit, sourceRung]);

  return (
    <FlowPickerMenu
      sections={sections}
      onChoose={(key) => {
        // The key carries the block and facet; the task comes from the section the row sits in,
        // so it is recovered by looking the block up rather than stuffing a third id into a
        // string that would then need parsing in two places.
        const [blockId, facet] = key.split(':');
        const task = tasks.find(tk => (tk.blocks ?? []).some(b => b.id === blockId));
        if (!task || !blockId || !facet) return;
        onChoose({ taskId: task.id, blockId, facet });
      }}
      ariaLabel={ariaLabel}
      filterPlaceholder={t('blocks.answerRef.filter')}
      noMatchesLabel={t('blocks.answerRef.noMatches')}
      renderTrigger={renderTrigger}
      handleRef={handleRef}
      onDismiss={onDismiss}
      className={className}
    />
  );
}
